import { useWeb3React as useWeb3ReactCore } from "@web3-react/core";
import { useCallback, useContext, useEffect, useState } from "react";
import { getContract } from "./web3";
import ERC20 from "./web3/abi/ERC20.json";
import {
  ANTIMATTER_TRANSACTION_LIST,
  HANDLE_POPUP_LIST,
  THIS_CHAIN_ID,
} from "./const";
import { mainContext } from "./reducer";
import GRANT from "./web3/abi/grant.json";
import DASHI from "./web3/abi/dashi.json";
import GRANT4_ABI from "./web3/abi/grant4.json";
import ANTIFRAUD from "./web3/abi/antifraud.json";
import BigNumber from "bignumber.js";
import {
  STAKE_ADDRESS,
  DASHI_ADDRESS,
  TRIAS4_CONTACT_ADDRESS,
  ANTI_CONTACT_ADDRESS,
} from "./web3/addresses";
import { Web3Provider } from "@ethersproject/providers";
import { NetworkConnector } from "./NetworkConnector.ts";

export const BSCNetwork = new NetworkConnector({
  urls: { 56: "https://bsc-dataseed4.binance.org" },
  // urls: { 56: 'https://bsc-dataseed3.binance.org' },
});
export const KovanNetwork = new NetworkConnector({
  urls: { 42: "https://kovan.infura.io/v3/de70061beb284ea3838537c6cf4f83d1" },
});
export const BSCTestNetwork = new NetworkConnector({
  urls: { 97: "https://data-seed-prebsc-1-s1.binance.org:8545" },
});

export function getNetworkLibrary(chainId) {
  switch (chainId) {
    case 56:
      return new Web3Provider(BSCNetwork.provider);
    case 42:
      return new Web3Provider(KovanNetwork.provider);
    case 97:
      return new Web3Provider(BSCTestNetwork.provider);
    default:
  }
}

export const useAllowance = (tokenAddress, address) => {
  const { account, active, library, chainId } = useWeb3ReactCore();
  const { blockNumber } = useBlockNumber();
  const [balance, setBalance] = useState();

  useEffect(() => {
    if (
      active &&
      chainId &&
      chainId === THIS_CHAIN_ID &&
      address &&
      tokenAddress
    ) {
      try {
        const contract = getContract(library, ERC20, tokenAddress);
        contract.allowance(account, address).then((res) => {
          setBalance(res.toString());
        });
      } catch (e) {
        console.log("load Allowance:", e);
      }
    } else {
      setBalance(undefined);
    }
  }, [active, chainId, address, blockNumber, account, tokenAddress, library]);

  return balance;
};

export const useBalance = (address) => {
  const { account, active, library, chainId } = useWeb3ReactCore();
  const { blockNumber } = useBlockNumber();
  const [balance, setBalance] = useState();

  useEffect(() => {
    if (active && chainId && chainId === THIS_CHAIN_ID && address) {
      try {
        const contract = getContract(library, ERC20, address);
        contract.balanceOf(account).then((res) => {
          setBalance(res.toString());
        });
      } catch (e) {
        console.log("load token balance error1:", address);
      }
    }
  }, [active, chainId, address, blockNumber, account, library]);

  return balance;
};

export const useDashiSupported = () => {
  const { account, active, library, chainId } = useWeb3ReactCore();
  const { blockNumber } = useBlockNumber();
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    if (active && chainId && chainId === THIS_CHAIN_ID) {
      try {
        const contract = getContract(library, DASHI, DASHI_ADDRESS[chainId]);
        contract.getStakeInfo(account).then((res) => {
          if (new BigNumber(res.toString()).isGreaterThan(1)) {
            setIsSupported(true);
          } else {
            setIsSupported(false);
          }
        });
      } catch (e) {
        console.log("useDashiSupported error:", e);
      }
    }
  }, [active, chainId, blockNumber, account, library]);

  return isSupported;
};

export const useStakerNumber = () => {
  const { library, chainId } = useWeb3ReactCore();
  const { blockNumber } = useBlockNumber();
  const [stakerNumber, setStakerNumber] = useState();

  useEffect(() => {
    if (chainId && chainId === THIS_CHAIN_ID && library) {
      const contract = getContract(library, DASHI, DASHI_ADDRESS[chainId]);
      try {
        contract.getStakerNumber().then((res) => {
          setStakerNumber(res.toString());
        });
      } catch (e) {
        console.log("load error:");
      }
    } else {
      const bscLibrary = getNetworkLibrary(THIS_CHAIN_ID);
      const contract = getContract(
        bscLibrary,
        DASHI,
        DASHI_ADDRESS[THIS_CHAIN_ID]
      );
      if (!bscLibrary || !contract) return;
      try {
        contract.getStakerNumber().then((res) => {
          setStakerNumber(res.toString());
        });
      } catch (e) {
        console.log("load error:");
      }
    }
  }, [chainId, blockNumber, library]);

  return stakerNumber;
};

// query votes
export const useDASHIVotes = (address) => {
  const { library, chainId, account } = useWeb3ReactCore();
  const { blockNumber } = useBlockNumber();
  const [IsVotes, setIsVotes] = useState();

  useEffect(() => {
    const _get_votes = async () => {
      if (!address) {
        setIsVotes(false);
        return;
      }
      if (chainId && chainId === THIS_CHAIN_ID && library) {
        const contract = getContract(
          library,
          DASHI,
          DASHI_ADDRESS[chainId],
          account
        );
        try {
          const res = await contract.votes(address, account);
          setIsVotes(res);
        } catch (e) {
          console.log("load error:", e);
          setIsVotes(false);
        }
      }
    };
    _get_votes();
  }, [address, account, chainId, library, blockNumber]);

  return IsVotes;
};

export const useStakingInfo = () => {
  const { library, chainId } = useWeb3ReactCore();
  const { blockNumber } = useBlockNumber();
  const [total, setTotal] = useState();
  const [top, setTop] = useState();

  useEffect(() => {
    if (chainId && chainId === THIS_CHAIN_ID && library) {
      const contract = getContract(library, GRANT, STAKE_ADDRESS[chainId]);

      try {
        contract.getTotalAmount().then((res) => {
          setTotal(res.toString());
        });
      } catch (e) {
        console.log("load error:");
      }

      try {
        contract.getHardTop().then((res) => {
          setTop(res.toString());
        });
      } catch (e) {
        console.log("load top error:", e);
      }
    }
  }, [chainId, blockNumber, library]);

  return { top, total };
};

export const useDASHIStakingInfo = () => {
  const { library, chainId } = useWeb3ReactCore();
  const { blockNumber } = useBlockNumber();
  const [total, setTotal] = useState();
  // const [top, setTop] = useState()

  useEffect(() => {
    if (chainId && chainId === THIS_CHAIN_ID && library) {
      const contract = getContract(library, DASHI, DASHI_ADDRESS[chainId]);
      try {
        contract.getTotalAmount().then((res) => {
          setTotal(res.toString());
        });
      } catch (e) {
        console.log("load error:");
      }

      // try {
      //   contract.getHardTop().then((res) => {
      //     setTop(res.toString())
      //   })
      // } catch (e) {
      //   console.log('load top error:', e)
      // }
    }
  }, [chainId, blockNumber, library]);

  return { total };
};

export const useBlockNumber = () => {
  const [blockNumber, setBlockNumber] = useState(0);
  const { library } = useWeb3ReactCore();
  const updateBlockNumber = (blockNumber) => {
    setBlockNumber(blockNumber);
  };

  useEffect(() => {
    if (library) {
      library.once("block", updateBlockNumber);
    }
    library && library.getBlockNumber().then((res) => {});
    return () => {
      library && library.off("block", updateBlockNumber);
    };
  }, [blockNumber, library]);

  return { blockNumber };
};

export const useTransactionAdder = () => {
  const { chainId } = useWeb3ReactCore();
  const { dispatch } = useContext(mainContext);
  return useCallback(
    (response, customData) => {
      if (!response) return;
      const { hash } = response;
      const { summary, stake, auctionStake, approve, huntervote, hashLink } =
        customData;
      const now = () => new Date().getTime();
      dispatch({
        type: ANTIMATTER_TRANSACTION_LIST,
        transaction: {
          hash,
          chainId,
          huntervote,
          auctionStake,
          summary,
          stake,
          approve,
          hashLink,
          addedTime: now(),
        },
      });
    },
    [chainId, dispatch]
  );
};

export const useRemovePopup = () => {
  const { dispatch } = useContext(mainContext);

  return useCallback(
    (key) => {
      dispatch({
        type: HANDLE_POPUP_LIST,
        auction: "remove",
        popup: { key: key, popKey: key, hash: key },
      });
    },
    [dispatch]
  );
};

export const TransactionsUpdater = () => {
  const { dispatch } = useContext(mainContext);
  const { chainId, library } = useWeb3ReactCore();
  const { blockNumber } = useBlockNumber();
  const { transactions } = useContext(mainContext).state;
  useEffect(() => {
    if (!chainId || !library || !blockNumber) return;
    transactions
      .filter((item) => {
        return (
          !item.receipt && new Date().getTime() - item.addedTime < 86_400_000
        );
      })
      .forEach((tx) => {
        library.getTransactionReceipt(tx.hash).then((receipt) => {
          if (receipt) {
            dispatch({
              type: ANTIMATTER_TRANSACTION_LIST,
              transaction: {
                ...tx,
                stake: tx.stake
                  ? { ...tx.stake, status: receipt.status === 1 ? 1 : -1 }
                  : null,
                huntervote: tx.huntervote
                  ? { ...tx.huntervote, status: receipt.status === 1 ? 1 : -1 }
                  : null,
                auctionStake: tx.auctionStake
                  ? {
                      ...tx.auctionStake,
                      status: receipt.status === 1 ? 1 : -1,
                    }
                  : null,
                claim: tx.claim
                  ? { ...tx.claim, status: receipt.status === 1 ? 1 : -1 }
                  : null,
                approve: tx.approve
                  ? { ...tx.approve, status: receipt.status === 1 ? 1 : -1 }
                  : null,
                exercise: tx.exercise
                  ? { ...tx.exercise, status: receipt.status === 1 ? 1 : -1 }
                  : null,
                receipt: {
                  blockHash: receipt.blockHash,
                  blockNumber: receipt.blockNumber,
                  contractAddress: receipt.contractAddress,
                  from: receipt.from,
                  status: receipt.status,
                  to: receipt.to,
                  transactionHash: tx.hash,
                  transactionIndex: receipt.transactionIndex,
                },
              },
            });
            dispatch({
              type: HANDLE_POPUP_LIST,
              auction: "add",
              popup: {
                key: receipt.transactionHash,
                popKey: receipt.transactionHash,
                hash: receipt.transactionHash,
                hashLink: tx.hashLink,
                summary: tx.summary,
                success: receipt.status === 1,
              },
            });
          } else {
          }
        });
      });
  }, [chainId, blockNumber, transactions, library, dispatch]);
  return null;
};

export const useNumberListVotes = () => {
  const [listVotes, setListVotes] = useState();
  const bscLibrary = getNetworkLibrary(56);

  const func = async () => {
    const contract = getContract(bscLibrary, DASHI, DASHI_ADDRESS[56]);
    if (!bscLibrary || !contract) return;
    try {
      const stakerNumbers = (await contract.getStakerNumber()).toString();
      const stakerAddresss = await Promise.all(
        Array.from(Array(parseInt(stakerNumbers)), (v, k) =>
          contract.stakers(k)
        )
      );
      let stakerVotes = await Promise.all(
        Array.from(Array(parseInt(stakerNumbers)), (v, k) =>
          contract.getVotes(k)
        )
      );
      stakerVotes = stakerVotes.map((v) => v.toString());

      const votesResult = stakerVotes.map((x) => {
        return new BigNumber(parseFloat(x))
          .multipliedBy(parseFloat(x))
          .dividedBy(10000)
          .integerValue(BigNumber.ROUND_CEIL)
          .toNumber();
      });

      let list = {};
      stakerAddresss.forEach((v, k) => {
        list[v] = votesResult[k];
      });
      setListVotes(list);
    } catch (e) {
      console.log("load error:", e);
    }
  }
  useEffect(
  func, [bscLibrary]);

  return listVotes;
};

export const useAntiFraudStakerNumber = () => {
  const { blockNumber } = useBlockNumber();
  const [antiFraudStakerNumber, setAntiFraudStakerNumber] = useState();

  useEffect(() => {
    const library = getNetworkLibrary(THIS_CHAIN_ID);
    const contract = getContract(
      library,
      ANTIFRAUD,
      ANTI_CONTACT_ADDRESS[THIS_CHAIN_ID]
    );
    if (!library || !contract) return;
    try {
      contract.getStakersNumber().then((res) => {
        setAntiFraudStakerNumber(res.toString());
      });
    } catch (e) {
      console.log("load error:", e);
    }
  }, [blockNumber]);

  return antiFraudStakerNumber;
};

export const useAntiFraudStakingExist = () => {
  const { account } = useWeb3ReactCore();
  const { blockNumber } = useBlockNumber();
  const [stakingExist, setStakingExist] = useState();

  useEffect(() => {
    const library = getNetworkLibrary(THIS_CHAIN_ID);
    const contract = getContract(
      library,
      ANTIFRAUD,
      ANTI_CONTACT_ADDRESS[THIS_CHAIN_ID]
    );
    if (!library || !contract || !account) return;
    try {
      contract.stakingExist(account).then((res) => {
        setStakingExist(res);
      });
    } catch (e) {
      console.log("load error:", e);
    }
  }, [account, blockNumber]);

  return stakingExist;
};

export const useAuctionStakeInfo = () => {
  const thisChain = THIS_CHAIN_ID === 56 ? 56 : 97
  const library = getNetworkLibrary(thisChain);
  const { blockNumber } = useBlockNumber();
  const [total, setTotal] = useState("");
  const [top, setTop] = useState("");

  useEffect(() => {
    if (library) {
      const contract = getContract(
        library,
        GRANT4_ABI,
        TRIAS4_CONTACT_ADDRESS[thisChain]
      );
      try {
        contract._hardTop().then((res) => {
          setTop(res.toString());
        });
        contract._totalAmount().then((res) => {
          setTotal(res.toString());
        });
      } catch (error) {
        console.log("useAuctionStakeInfo", error);
      }
    }
  }, [blockNumber, library, thisChain]);

  return { total, top };
};
